import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthWrapper';
import {
  CheckCircle2, XCircle, CreditCard, Loader2, AlertCircle,
  Building2, FileText, Download, Save, Receipt
} from 'lucide-react';
import SharedHeader from './SharedHeader';

const BillingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [profile, setProfile] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [error, setError] = useState('');

  // Profile form state
  const [formData, setFormData] = useState({
    billing_company: '',
    billing_gstin: '',
    billing_address_line1: '',
    billing_address_line2: '',
    billing_city: '',
    billing_state: '',
    billing_pincode: '',
    billing_country: 'India',
    billing_phone: '',
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const getBaseUrl = () => process.env.REACT_APP_API_BASE_URL || '';
  const getToken = () => {
    const cookies = document.cookie.split(';').map(c => c.trim());
    const sessionCookie = cookies.find(c => c.startsWith('session_token='));
    return sessionCookie ? sessionCookie.split('=')[1] : '';
  };

  const fetchAll = async () => {
    try {
      const token = getToken();
      const headers = { 'Authorization': `Bearer ${token}` };

      const [statusResp, profileResp, invoicesResp] = await Promise.all([
        fetch(`${getBaseUrl()}/api/billing/status`, { headers }),
        fetch(`${getBaseUrl()}/api/billing/profile`, { headers }),
        fetch(`${getBaseUrl()}/api/billing/invoices`, { headers }),
      ]);

      if (statusResp.ok) setStatus(await statusResp.json());
      if (profileResp.ok) {
        const p = await profileResp.json();
        setProfile(p);
        setFormData({
          billing_company: p.billing_company || '',
          billing_gstin: p.billing_gstin || '',
          billing_address_line1: p.billing_address_line1 || '',
          billing_address_line2: p.billing_address_line2 || '',
          billing_city: p.billing_city || '',
          billing_state: p.billing_state || '',
          billing_pincode: p.billing_pincode || '',
          billing_country: p.billing_country || 'India',
          billing_phone: p.billing_phone || '',
        });
      }
      if (invoicesResp.ok) {
        const invData = await invoicesResp.json();
        setInvoices(invData.invoices || []);
        setPayments(invData.payments || []);
      }
    } catch (err) {
      setError('Could not load billing data.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setProfileSaved(false);
    setError('');
    try {
      const resp = await fetch(`${getBaseUrl()}/api/billing/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`,
        },
        body: JSON.stringify(formData),
      });
      const data = await resp.json();
      if (resp.ok) {
        setProfileSaved(true);
        setTimeout(() => setProfileSaved(false), 3000);
      } else {
        setError(data.detail || 'Failed to save billing profile');
      }
    } catch (err) {
      setError('Could not save billing profile.');
    }
    setSavingProfile(false);
  };

  const handleCreateInvoice = async (paymentId) => {
    try {
      const resp = await fetch(`${getBaseUrl()}/api/billing/invoice/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ payment_id: paymentId }),
      });
      const data = await resp.json();
      if (resp.ok && data.short_url) {
        window.open(data.short_url, '_blank');
      } else {
        setError(data.detail || 'Could not create invoice');
      }
    } catch (err) {
      setError('Could not create invoice.');
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    return new Date(ts * 1000).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  const formatAmount = (amount, currency) => {
    if (currency === 'INR') return `₹${(amount / 100).toFixed(2)}`;
    return `${(amount / 100).toFixed(2)} ${currency}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a1525' }}>
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  const isActive = status?.subscription_status === 'active';

  return (
    <div className="min-h-screen" style={{ background: '#0a1525' }}>
      <SharedHeader activePage="billing" />

      {/* ─── Content ─── */}
      <div className="pt-24 pb-16 max-w-4xl mx-auto px-4">
        <h1 className="text-2xl font-bold text-white mb-6">Billing & Subscription</h1>

        {error && (
          <div className="mb-6 bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* ─── Current Plan ─── */}
        <div className="bg-slate-900/80 backdrop-blur-md rounded-2xl border border-slate-700/50 p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-400" />
            Current Plan
          </h2>
          <div className="space-y-3">
            <Row label="Plan" value={<span className="capitalize font-medium text-white">{status?.plan || 'free'}</span>} />
            <Row label="Status" value={
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                isActive ? 'bg-green-500/20 text-green-400' :
                status?.subscription_status === 'canceled' ? 'bg-red-500/20 text-red-400' :
                'bg-slate-700 text-slate-400'
              }`}>
                {status?.subscription_status || 'none'}
              </span>
            } />
            <Row label="Billing Type" value={
              status?.billing_type === 'subscription' ? 'Auto-pay monthly' :
              status?.billing_type === 'one_time' ? 'One-time payment' : 'None'
            } />
            {status?.renews_at && (
              <Row label="Renews At" value={new Date(status.renews_at).toLocaleDateString()} />
            )}
            <Row label="Monthly Request Limit" value={
              status?.monthly_request_limit === -1 ? 'Unlimited' : `${status?.monthly_request_limit || 250}/month`
            } />
            <Row label="Requests Used" value={`${status?.requests_used_this_period || 0} this period`} />
          </div>
          {status?.plan === 'free' && (
            <button onClick={() => navigate('/pricing')} className="w-full mt-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
              <CreditCard className="w-4 h-4" /> Upgrade Plan
            </button>
          )}
        </div>

        {/* ─── Billing Profile (GST + Address) ─── */}
        <div className="bg-slate-900/80 backdrop-blur-md rounded-2xl border border-slate-700/50 p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-400" />
            Billing Profile (GST & Address)
          </h2>
          <p className="text-slate-400 text-sm mb-4">
            This information appears on your GST invoices. Fill this in before making a payment for GST-compliant invoices.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Company Name" value={formData.billing_company} onChange={v => setFormData({...formData, billing_company: v})} placeholder="Acme Corp Pvt Ltd" />
            <Input label="GSTIN" value={formData.billing_gstin} onChange={v => setFormData({...formData, billing_gstin: v.toUpperCase()})} placeholder="29ABCDE1234F1Z5" maxLength={15} />
            <div className="md:col-span-2">
              <Input label="Address Line 1" value={formData.billing_address_line1} onChange={v => setFormData({...formData, billing_address_line1: v})} placeholder="123, Industrial Area" />
            </div>
            <div className="md:col-span-2">
              <Input label="Address Line 2" value={formData.billing_address_line2} onChange={v => setFormData({...formData, billing_address_line2: v})} placeholder="Building, Floor (optional)" />
            </div>
            <Input label="City" value={formData.billing_city} onChange={v => setFormData({...formData, billing_city: v})} placeholder="Bengaluru" />
            <Input label="State" value={formData.billing_state} onChange={v => setFormData({...formData, billing_state: v})} placeholder="Karnataka" />
            <Input label="Pincode" value={formData.billing_pincode} onChange={v => setFormData({...formData, billing_pincode: v.replace(/\D/g, '')})} placeholder="560001" maxLength={6} />
            <Input label="Phone" value={formData.billing_phone} onChange={v => setFormData({...formData, billing_phone: v})} placeholder="+91 98765 43210" />
          </div>

          <div className="flex items-center gap-3 mt-6">
            <button
              onClick={handleSaveProfile}
              disabled={savingProfile}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {savingProfile ? 'Saving...' : 'Save Billing Profile'}
            </button>
            {profileSaved && (
              <span className="text-green-400 text-sm flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> Saved!
              </span>
            )}
          </div>
        </div>

        {/* ─── Invoices & Payment History ─── */}
        <div className="bg-slate-900/80 backdrop-blur-md rounded-2xl border border-slate-700/50 p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-blue-400" />
            Payment History & Invoices
          </h2>

          {invoices.length === 0 && payments.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No payments yet. Your payment history and invoices will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Invoices */}
              {invoices.map((inv) => (
                <div key={inv.invoice_id} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-white">Invoice #{inv.invoice_number || inv.invoice_id.slice(-8)}</p>
                      <p className="text-xs text-slate-500">{formatDate(inv.created_at)} · {formatAmount(inv.amount, inv.currency)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${inv.paid ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      {inv.status}
                    </span>
                    {inv.short_url && (
                      <a href={inv.short_url} target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg transition-colors">
                        <Download className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              ))}

              {/* Payments (without invoice) */}
              {payments.filter(p => !p.invoice_id).map((pay) => (
                <div key={pay.payment_id} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-white">{formatAmount(pay.amount, pay.currency)}</p>
                      <p className="text-xs text-slate-500">{formatDate(pay.created_at)} · {pay.method || 'card'} · {pay.payment_id.slice(-8)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleCreateInvoice(pay.payment_id)}
                    className="px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5"
                  >
                    <FileText className="w-4 h-4" />
                    Get Invoice
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Back button */}
        <button onClick={() => navigate('/dashboard')} className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors">
          Back to Dashboard
        </button>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-center py-2 border-b border-slate-800 last:border-0">
    <span className="text-slate-400 text-sm">{label}</span>
    <span className="text-slate-200 text-sm">{value}</span>
  </div>
);

const Input = ({ label, value, onChange, placeholder, maxLength }) => (
  <div>
    <label className="text-xs text-slate-400 mb-1 block">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white text-sm placeholder-slate-600 focus:outline-none focus:border-blue-500 transition-colors"
    />
  </div>
);

export default BillingPage;
